import { Play, Pause, RotateCcw, ArrowUp, ArrowDown, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import useTimer, { type TimerStatus } from "@/lib/timer";
import { loadConfig } from "@/lib/config";
import { useEffect, useState } from "react";


function formatTime(totalSeconds: number): string {
	const m = Math.floor(totalSeconds / 60);
	const s = totalSeconds % 60;
	return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

const stateStyles = {
	idle: { label: "Ready", color: "text-muted-foreground", bg: "" },
	working: { label: "Focus", color: "text-ember", bg: "bg-ember-glow" },
	paused: { label: "Paused", color: "text-muted-foreground", bg: "" },
	short_break: { label: "Break", color: "text-honey", bg: "bg-honey-glow" },
	movement_break: { label: "Move", color: "text-grove", bg: "bg-grove-glow" },
} satisfies Record<TimerStatus, { label: string; color: string; bg: string }>;

function CycleDots({ completed, active, total }: { completed: number; active: boolean; total: number }) {
	return (
		<div className="flex gap-1.5">
			{Array.from({ length: total }, (_, i) => {
				const done = i < completed;
				const current = active && i === completed;
				return (
					<div
						key={i}
						className={`size-1.5 rounded-full transition-colors duration-500 ${done ? "bg-ember" : current ? "bg-ember/40" : "bg-border"
							}`}
					/>
				);
			})}
		</div>
	);
}

export function Timer({ onShowConfig }: { onShowConfig: () => void }) {
	const [config] = useState(() => loadConfig());
	const timer = useTimer(config);
	const style = stateStyles[timer.status];

	const cycleTotal = config.movementBreakFrequency;
	const isMovementBreak = timer.status === "movement_break";
	const cycleCompleted = isMovementBreak ? cycleTotal : timer.currentPomodoro % cycleTotal;
	const isActive = timer.status === "working" || timer.status === "paused";

	useEffect(() => {
	  if (timer.status === "idle") {
		document.title = "Pomotion";
	    return;
	  }

	  document.title = `${formatTime(timer.secondsRemaining)} ${style.label} | Pomotion`;
	}, [timer.secondsRemaining, timer.status]);

	return (
		<>
			{/* Session history - earned pomodoros */}
			{timer.currentPomodoro > 0 && (
				<div className="flex flex-col items-center gap-2 mb-6">
					<div className="flex items-baseline gap-1.5">
						<span className="font-medium text-2xl tabular-nums text-ember">
							{timer.currentPomodoro}
						</span>
						<span className="text-[0.6875rem] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
							today
						</span>
					</div>
					<div className="flex flex-wrap justify-center gap-1 max-w-[240px]">
						{Array.from({ length: timer.currentPomodoro }, (_, i) => (
							<div
								key={i}
								className="w-1.5 h-2 rounded-[1px] bg-ember/60"
							/>
						))}
					</div>
				</div>
			)}

			<div
				className={`relative w-full max-w-xs rounded-2xl border px-8 py-10 transition-colors duration-700 ease-out ${style.bg || "bg-card"
					}`}
			>
				<button
					onClick={onShowConfig}
					className="absolute top-3 left-3 text-muted-foreground hover:text-foreground transition-colors"
					aria-label="Settings"
				>
					<Settings className="size-3.5" />
				</button>

				<p className={`text-center text-[0.6875rem] font-semibold uppercase tracking-[0.25em] ${style.color}`}>
					{style.label}
				</p>
				<p className="mt-6 text-center font-bold tabular-nums text-7xl tracking-tighter text-foreground">
					{formatTime(timer.secondsRemaining)}
				</p>

				{timer.status !== "idle" && (
					<div className="mt-6 flex items-center justify-center gap-4">
						<CycleDots completed={cycleCompleted} active={isActive} total={cycleTotal} />
						<span className="flex items-center gap-1 text-xs text-muted-foreground">
							{timer.deskPosition === "stand" ? (
								<ArrowUp className="size-3" />
							) : (
								<ArrowDown className="size-3" />
							)}
							{timer.deskPosition === "stand" ? "Standing" : "Sitting"}
						</span>
					</div>
				)}

				<div className="mt-8 flex flex-col items-center gap-3">
					{timer.status === "idle" && (
						<Button
							onClick={timer.start}
							size="lg"
							className="bg-ember text-white hover:bg-ember/85"
						>
							<Play className="size-4" />
							Start
						</Button>
					)}

					{timer.status === "working" && (
						<Button onClick={timer.pause} variant="outline" size="lg">
							<Pause className="size-4" />
							Pause
						</Button>
					)}

					{timer.status === "paused" && (
						<>
							<button
								onClick={timer.reset}
								className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
							>
								<RotateCcw className="size-3.5" />
							</button>
							<Button
								onClick={timer.resume}
								size="lg"
								className="bg-ember text-white hover:bg-ember/85"
							>
								<Play className="size-4" />
								Resume
							</Button>
						</>
					)}

					{timer.status === "short_break" && (
						<p className="text-sm text-honey">Breathe. Next session soon.</p>
					)}

					{timer.status === "movement_break" && (
						<>
							{timer.currentMovement && (
								<div className="text-center">
									<h2 className="text-lg font-semibold text-grove">{timer.currentMovement.name}</h2>
									<p className="mt-1 text-sm text-muted-foreground">{timer.currentMovement.description}</p>
								</div>
							)}

							<Button
								onClick={timer.markMovementDone}
								size="lg"
								className="bg-grove text-white hover:bg-grove/85"
							>
								Done
							</Button>
						</>
					)}
				</div>
			</div>
		</>
	);
}
